/**
 * Default site-level title and description.
 *
 * Computed from the dataset registry, never hardcoded, so the headline numbers
 * in search results cannot drift from what the catalogue actually contains.
 *
 * This module is imported by BOTH:
 *   - src/app/index.tsx                         (the React app's default head)
 *   - internals/scripts/generate-seo-assets.ts  (the static public/index.html)
 * so the shell HTML and the hydrated app always agree. Keep it dependency-free
 * apart from the registry — the generator runs it under ts-node outside webpack.
 *
 * What was here before:
 *   <title>SOVAI</title>  — five characters, no keyword, no category
 *   description: "The only machine learning software geared towards long-term
 *   investment management." — never uses the words "data", "dataset" or
 *   "catalogue", so it could not match the queries this site should win.
 */

// Relative import, not the `app/...` alias: this module is also loaded by
// internals/scripts/generate-seo-assets.ts under ts-node, which has no webpack
// baseUrl resolution. Keep it relative or the sitemap generator breaks.
import { catalogueStats } from '../../data/datasets';

const stats = catalogueStats();

/** First four digits of the earliest published history date, e.g. "1985". */
export const EARLIEST_HISTORY_YEAR = stats.earliestHistory.slice(0, 4);

/**
 * Homepage / default title. Leads with the category ("Data Catalogue") and the
 * qualifier that distinguishes this from a generic data vendor ("ticker-linked",
 * "for asset managers"). Roughly 95 characters — long for a SERP, but the front
 * half carries the meaning if Google truncates it.
 */
export const SITE_TITLE = `SOVAI Data Catalogue — ${stats.total} Ticker-Linked Alternative Datasets for Asset Managers`;

/**
 * Default meta description. ~230 characters; Google will truncate around 155–160
 * on desktop, so the counts and the "back to <year>" claim are front-loaded.
 */
export const SITE_DESCRIPTION =
  `Browse ${stats.total} ticker-linked alternative datasets for asset managers: ` +
  `${stats.categories.equity} equity, ${stats.categories.economic} economic and ` +
  `${stats.categories.sectorial} sectorial, with history back to ${EARLIEST_HISTORY_YEAR}. ` +
  `Queried from Python.`;

/** Title suffix applied to per-page titles by the app-level Helmet. */
export const TITLE_TEMPLATE = '%s | SOVAI Data Catalogue';

export const SITE_KEYWORDS = [
  'alternative data',
  'financial datasets',
  'data catalogue',
  'ticker-linked data',
  'quantitative research',
  'asset management',
  'systematic investing',
  'Python financial data API',
];
